import { useEffect, useCallback, useRef, useState } from 'react';
import { RefreshCw, Download, CheckCircle, RefreshCw as ReplaceIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { DatePresetMenu } from '@/features/kiosk-sessions/components/DatePresetMenu';
import { ActorSelector } from '@/core/components/ActorSelector';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useDataErrorStore } from '../../stores/data-error-store';
import { IrregularDeltaTable } from '../organisms/IrregularDeltaTable';
import { MeterValuesBlade } from '../organisms/MeterValuesBlade';
import { AnalyzeAllBlade } from '../organisms/AnalyzeAllBlade';
import type { HandleType, IrregularDeltaItem } from '../../types/data-error-types';
import { cn } from '@/core/lib/utils';

// ── Multi-select facet filter popover ─────────────────────────────────────────

interface FacetFilterProps {
  label: string;
  options: { key: string; label: string; count: number }[];
  selected: string[];
  onChange: (keys: string[]) => void;
}

function FacetFilter({ label, options, selected, onChange }: FacetFilterProps) {
  const [open, setOpen] = useState(false);

  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
  };

  const displayLabel =
    selected.length === 0
      ? label
      : selected.length === 1
      ? (options.find(o => o.key === selected[0])?.label ?? selected[0])
      : `${label} (${selected.length})`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 text-xs border-[#E8E8E8] text-[#262626] font-normal',
            selected.length > 0 && 'border-[#FFCD00] bg-[#FFFBEB]'
          )}
        >
          {displayLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="start">
        {options.length === 0 && (
          <p className="text-xs text-[#606060] px-2 py-1">No options</p>
        )}
        {options.map(opt => (
          <label
            key={opt.key}
            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#FAFAFA] cursor-pointer text-sm"
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded accent-[#FFCD00]"
              checked={selected.includes(opt.key)}
              onChange={() => toggle(opt.key)}
            />
            <span className="flex-1 text-[#262626]">{opt.label}</span>
            <span className="text-xs text-[#606060]">{opt.count}</span>
          </label>
        ))}
        {selected.length > 0 && (
          <button
            className="w-full mt-1 text-xs text-[#606060] hover:text-[#262626] text-left px-2 py-1"
            onClick={() => onChange([])}
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Batch action toolbar ──────────────────────────────────────────────────────

interface BatchBarProps {
  count: number;
  onAction: (type: HandleType) => void;
  onClear: () => void;
}

function BatchBar({ count, onAction, onClear }: BatchBarProps) {
  if (count === 0) return null;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-[#FFFBEB] border-b border-[#FFCD00]">
      <span className="text-xs font-medium text-[#606060]">{count} selected:</span>
      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-green-700 border-green-200 hover:bg-green-50" onClick={() => onAction('Approve')}>
        <CheckCircle className="h-3.5 w-3.5" /> Approve
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-blue-700 border-blue-200 hover:bg-blue-50" onClick={() => onAction('Replacement')}>
        <ReplaceIcon className="h-3.5 w-3.5" /> Replacement
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onAction('Delete')}>
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </Button>
      <button onClick={onClear} className="ml-auto text-xs text-[#606060] hover:text-[#262626] underline">
        Clear selection
      </button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DataErrorPage() {
  const { isDistributor } = useAuthStore();
  const {
    operators,
    selectedOperator,
    selectOperator,
    isLoading: operatorsLoading,
    loadOperators,
  } = useOperatorStore();

  const {
    items, totalCount, currentPage, pageSize,
    isLoading, error, filters,
    statusOptions, locationUsageOptions,
    selectedItem, meterValues, isLoadingMeterValues, meterValuesError, surroundSize,
    handlingId, checkedIds, isExporting, isRebuildingPayments,
    loadItems, loadFilters, loadPage, setPageSize, setFilters,
    selectItem, setSurroundSize,
    handleItem, batchHandle, updateComment,
    toggleChecked, clearChecked, exportToExcel,
    rebuildPayments, openGroupAnalysis,
  } = useDataErrorStore();

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchInput, setSearchInput] = useState(filters.search);
  const [grouped, setGrouped] = useState(true);

  const handleAnalyzeAll = useCallback((items: IrregularDeltaItem[]) => {
    openGroupAnalysis(items);
  }, [openGroupAnalysis]);

  // Load operators list for distributor dropdown
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !operatorsLoading) {
      loadOperators();
    }
  }, [isDistributor, operators.length, operatorsLoading, loadOperators]);

  // Bootstrap on mount
  useEffect(() => {
    loadFilters().then(() => loadItems());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up search debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, []);

  const handleOperatorChange = useCallback((operatorId: string) => {
    const operator = operators.find(op => op.operatorId.toString() === operatorId);
    if (operator) {
      selectOperator(operator);
      selectItem(null);
      clearChecked();
      setSearchInput('');
      loadFilters().then(() => loadItems());
    }
  }, [operators, selectOperator, selectItem, clearChecked, loadFilters, loadItems]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setFilters({ search: value });
    }, 250);
  }, [setFilters]);

  const handleDateChange = useCallback((range: { from: string; to: string }) => {
    setFilters({ periodFrom: range.from, periodTo: range.to });
  }, [setFilters]);

  // Table row action — no auto-advance (blade may not be open)
  const handleTableAction = useCallback(async (irregularId: number, type: HandleType) => {
    await handleItem(irregularId, type, '');
    if (!useDataErrorStore.getState().error) toast.success('Record updated');
    else toast.error(useDataErrorStore.getState().error ?? 'Action failed');
  }, [handleItem]);

  // Blade action — after success, auto-advance to the next pending record from the same charger
  const handleBladeAction = useCallback(async (irregularId: number, type: HandleType) => {
    const machineId = useDataErrorStore.getState().selectedItem?.machineId;
    await handleItem(irregularId, type, '');

    if (useDataErrorStore.getState().error) {
      toast.error(useDataErrorStore.getState().error ?? 'Action failed');
      return;
    }
    toast.success('Record updated');

    // items is now fresh — handleItem awaits loadItems() internally
    const nextPending = machineId
      ? useDataErrorStore.getState().items.find(i => i.machineId === machineId && !i.handled)
      : undefined;
    selectItem(nextPending ?? null);
  }, [handleItem, selectItem]);

  const handleBatchAction = useCallback(async (type: HandleType) => {
    const count = checkedIds.size;
    await batchHandle(type, '');
    if (!useDataErrorStore.getState().error) toast.success(`${count} records updated`);
    else toast.error(useDataErrorStore.getState().error ?? 'Batch action failed');
  }, [batchHandle, checkedIds.size]);

  const handleExport = useCallback(async () => {
    await exportToExcel();
    if (!useDataErrorStore.getState().error) toast.success('Export ready');
    else toast.error(useDataErrorStore.getState().error ?? 'Export failed');
  }, [exportToExcel]);

  const hasActiveFilters =
    filters.search ||
    filters.selectedStatuses.length > 0 ||
    filters.selectedLocationUsages.length > 0 ||
    filters.actorId != null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0">

        {/* Row 1 — operator selector (distributors only) + period + reset */}
        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-white border-b border-[#E8E8E8]">
          {isDistributor && (
            <ActorSelector
              operators={operators}
              selectedOperatorId={selectedOperator?.operatorId?.toString() ?? ''}
              onOperatorChange={handleOperatorChange}
              isLoading={operatorsLoading}
              className="h-8 min-w-[180px] max-w-[260px] border-[#E8E8E8]"
            />
          )}
          <DatePresetMenu
            value={{ from: filters.periodFrom ?? '', to: filters.periodTo ?? '' }}
            onChange={handleDateChange}
          />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-[#606060] hover:text-[#262626]"
              onClick={() => {
                setSearchInput('');
                setFilters({ search: '', selectedStatuses: [], selectedLocationUsages: [], actorId: null });
              }}
            >
              Reset filters
            </Button>
          )}
        </div>

        {/* Row 2 — search + facet filters */}
        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-[#FAFAFA] border-b border-[#E8E8E8]">
          <Input
            placeholder="Search by name or machine ID"
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            className="h-8 w-56 text-xs border-[#E8E8E8]"
          />
          <FacetFilter
            label="Status"
            options={statusOptions}
            selected={filters.selectedStatuses}
            onChange={keys => setFilters({ selectedStatuses: keys })}
          />
          <FacetFilter
            label="Location Usage"
            options={locationUsageOptions}
            selected={filters.selectedLocationUsages}
            onChange={keys => setFilters({ selectedLocationUsages: keys })}
          />
        </div>

        {/* Row 3 — count + export + refresh */}
        <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-[#FAFAFA] border-b border-[#E8E8E8]">
          <span className="text-sm text-[#606060] font-medium">
            {totalCount.toLocaleString()} record{totalCount !== 1 ? 's' : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] border-[#FFCD00] font-medium"
              disabled={isRebuildingPayments}
              onClick={async () => {
                await rebuildPayments();
                const err = useDataErrorStore.getState().error;
                if (!err) toast.success('Payment rebuild started');
                else toast.error(err);
              }}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRebuildingPayments && 'animate-spin')} />
              {isRebuildingPayments ? 'Rebuilding…' : 'Rebuild Payments'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs border-[#E8E8E8] text-[#262626]"
              disabled={isExporting}
              onClick={handleExport}
            >
              <Download className="h-3.5 w-3.5" />
              {isExporting ? 'Exporting…' : 'Export xlsx'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 border-[#E8E8E8] text-[#262626]"
              onClick={() => { loadFilters(); loadItems(); }}
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Batch action bar */}
        <BatchBar
          count={checkedIds.size}
          onAction={handleBatchAction}
          onClear={clearChecked}
        />
      </div>

      {/* ── Grid ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-hidden px-6 py-0">
        <IrregularDeltaTable
          items={items}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          isLoading={isLoading}
          error={error}
          selectedItemId={selectedItem?.irregularId ?? null}
          handlingId={handlingId}
          checkedIds={checkedIds}
          grouped={grouped}
          onGroupedChange={setGrouped}
          onRowClick={selectItem}
          onHandle={handleTableAction}
          onCommentBlur={updateComment}
          onToggleChecked={toggleChecked}
          onPageChange={loadPage}
          onPageSizeChange={setPageSize}
          onAnalyzeAll={handleAnalyzeAll}
        />
      </div>

      {/* ── Meter values blade ──────────────────────────────────────────────── */}
      <MeterValuesBlade
        open={!!selectedItem}
        item={selectedItem}
        meterValues={meterValues}
        isLoading={isLoadingMeterValues}
        error={meterValuesError}
        surroundSize={surroundSize}
        handlingId={handlingId}
        onClose={() => selectItem(null)}
        onSurroundSizeChange={setSurroundSize}
        onRetry={() => selectedItem && setSurroundSize(surroundSize)}
        onHandle={handleBladeAction}
      />

      {/* ── Analyze all blade ───────────────────────────────────────────────── */}
      <AnalyzeAllBlade />
    </div>
  );
}
